import debounce from 'lodash.debounce';
import { useEffect, useState } from 'react';
import { SYS_SPACE_FIXED_300 } from '@oneaudi/unified-web-alpha-common';
const updatePopoverPosition = ({ ref, triggerRef, alignment, withTip, placement }) => {
    const popover = ref.current;
    const trigger = triggerRef.current;
    if (!popover || !trigger)
        return;
    const root = document.documentElement;
    const pM = parseInt(window.getComputedStyle(root).getPropertyValue('--page-margin'));
    popover.style.left = '';
    popover.style.right = '';
    const triggerRect = trigger.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const windowInnerWidth = window.innerWidth;
    const windowInnerHeight = window.innerHeight;
    // Tip should be disabled on small screens
    const tipEnabled = Boolean(withTip) && windowInnerWidth >= 768;
    if (!tipEnabled) {
        popover.style.setProperty('--tip-left', 'unset');
        popover.style.setProperty('--tip-right', 'unset');
        popover.style.setProperty('--tip-top', 'unset');
    }
    const triggerLeft = Math.round(triggerRect.left);
    const triggerRight = Math.round(triggerRect.right);
    const triggerWidth = Math.round(triggerRect.width);
    const popoverWidth = Math.round(popoverRect.width);
    // TODO: Update pageMarginOffset when design supplies value
    const pageMarginOffset = 0;
    const tipWidth = 23;
    const tipHeight = 11.5;
    const tipEdgePadding = SYS_SPACE_FIXED_300;
    const tipEdgePaddingPx = typeof tipEdgePadding === 'number'
        ? tipEdgePadding
        : parseInt(String(tipEdgePadding), 10);
    const clampTipOffsetX = (desiredOffset) => {
        const max = popoverWidth - tipEdgePaddingPx - tipWidth;
        // If the popover is too narrow to respect the edge padding on both sides,
        // center the tip as a graceful fallback.
        if (max <= tipEdgePaddingPx) {
            return Math.max(0, Math.round((popoverWidth - tipWidth) / 2));
        }
        return Math.min(Math.max(desiredOffset, tipEdgePaddingPx), max);
    };
    let leftPosition = triggerLeft;
    let rightPosition = windowInnerWidth - triggerRight;
    const tipTriggerAlignValue = triggerWidth / 2 - tipWidth / 2;
    const getTipCentered = (tipLeft) => tipLeft + tipTriggerAlignValue;
    const popoverOffset = windowInnerWidth - popoverWidth;
    let popoverTop;
    let actualPlacement;
    const tipOffset = 20;
    const tipTopPlacement = popoverRect.height;
    const tipBottomPlacement = -tipHeight;
    const hasRoomAbove = triggerRect.top - tipOffset - popoverRect.height >= 0;
    const hasRoomBelow = triggerRect.bottom + tipOffset + popoverRect.height <= windowInnerHeight;
    const placeAbove = (placement === 'top' && hasRoomAbove) ||
        (placement === 'bottom' && !hasRoomBelow);
    if (placeAbove) {
        popoverTop = triggerRect.top - tipOffset - popoverRect.height;
        if (tipEnabled) {
            popover.style.setProperty('--tip-top', `${tipTopPlacement}px`);
        }
        else {
            popover.style.setProperty('--tip-top', 'unset');
        }
        actualPlacement = 'top';
    }
    else {
        popoverTop = triggerRect.bottom + tipOffset;
        if (tipEnabled) {
            popover.style.setProperty('--tip-top', `${tipBottomPlacement}px`);
        }
        else {
            popover.style.setProperty('--tip-top', 'unset');
        }
        actualPlacement = 'bottom';
    }
    popover.style.top = `${popoverTop}px`;
    if (windowInnerWidth < 768) {
        popover.style.left = `${pM + pageMarginOffset}px`;
        popover.style.right = `${pM + pageMarginOffset}px`;
        return actualPlacement;
    }
    switch (alignment) {
        case 'left': {
            leftPosition = triggerLeft;
            if (leftPosition + popoverWidth > windowInnerWidth) {
                leftPosition = popoverOffset;
            }
            if (leftPosition < 0) {
                leftPosition = 0;
            }
            popover.style.left = `${leftPosition}px`;
            if (tipEnabled) {
                // Compute desired/clamped tip offset
                let desiredLeft = triggerLeft - leftPosition + tipTriggerAlignValue;
                let clampedLeft = clampTipOffsetX(desiredLeft);
                // Shift popover left by the absolute difference
                const shift = Math.abs(clampedLeft - desiredLeft);
                if (shift > 0) {
                    const minLeft = 0;
                    const maxLeft = windowInnerWidth - popoverWidth;
                    leftPosition = Math.min(Math.max(leftPosition - shift, minLeft), maxLeft);
                    popover.style.left = `${leftPosition}px`;
                    // Recalculate offsets after shifting
                    desiredLeft = triggerLeft - leftPosition + tipTriggerAlignValue;
                    clampedLeft = clampTipOffsetX(desiredLeft);
                }
                popover.style.setProperty('--tip-right', 'unset');
                popover.style.setProperty('--tip-left', `${clampedLeft}px`);
            }
            break;
        }
        case 'right': {
            rightPosition = windowInnerWidth - triggerRight;
            if (rightPosition + popoverWidth > windowInnerWidth) {
                rightPosition = popoverOffset;
            }
            if (rightPosition < 0) {
                rightPosition = 0;
            }
            popover.style.right = `${rightPosition}px`;
            if (tipEnabled) {
                let desiredRight = windowInnerWidth -
                    (rightPosition + triggerRight) +
                    tipTriggerAlignValue;
                let clampedRight = clampTipOffsetX(desiredRight);
                // Shift popover right by the absolute difference
                const shift = Math.abs(clampedRight - desiredRight);
                if (shift > 0) {
                    const minRight = 0;
                    const maxRight = popoverOffset;
                    rightPosition = Math.min(Math.max(rightPosition - shift, minRight), maxRight);
                    popover.style.right = `${rightPosition}px`;
                    // Recalculate offsets after shifting
                    desiredRight =
                        windowInnerWidth -
                            (rightPosition + triggerRight) +
                            tipTriggerAlignValue;
                    clampedRight = clampTipOffsetX(desiredRight);
                }
                popover.style.setProperty('--tip-left', 'unset');
                popover.style.setProperty('--tip-right', `${clampedRight}px`);
            }
            break;
        }
        case 'center': {
            let leftPosForCenteredPopover = triggerLeft + triggerWidth / 2 - popoverWidth / 2;
            if (leftPosForCenteredPopover + popoverWidth > windowInnerWidth) {
                leftPosForCenteredPopover = popoverOffset;
            }
            if (leftPosForCenteredPopover < 0) {
                leftPosForCenteredPopover = 0;
            }
            popover.style.left = `${leftPosForCenteredPopover}px`;
            if (tipEnabled) {
                const tipLeft = triggerLeft - leftPosForCenteredPopover;
                const desiredLeft = getTipCentered(tipLeft);
                const clampedLeft = clampTipOffsetX(desiredLeft);
                popover.style.setProperty('--tip-right', 'unset');
                popover.style.setProperty('--tip-left', `${clampedLeft}px`);
            }
            break;
        }
    }
    return actualPlacement;
};
export const usePopoverPosition = ({ ref, triggerRef, alignment, placement, open, withTip }) => {
    const [actualPlacement, setActualPlacement] = useState(null);
    useEffect(() => {
        const debouncedUpdatePopoverPosition = debounce(() => {
            const newPlacement = updatePopoverPosition({
                ref,
                triggerRef,
                alignment,
                placement,
                withTip
            });
            setActualPlacement(newPlacement ?? null);
        }, 80);
        const handleFontsLoaded = () => {
            const newPlacement = updatePopoverPosition({
                ref,
                triggerRef,
                alignment,
                placement,
                withTip
            });
            setActualPlacement(newPlacement ?? null);
        };
        // Use event listener to avoid overwriting any global handler
        try {
            document.fonts.addEventListener?.('loadingdone', handleFontsLoaded);
        }
        catch {
            // no-op if fonts API not supported
        }
        const initialPlacement = updatePopoverPosition({
            triggerRef,
            alignment,
            placement,
            ref,
            withTip
        });
        setActualPlacement(initialPlacement ?? null);
        const handleResize = () => {
            debouncedUpdatePopoverPosition();
        };
        window.addEventListener('resize', handleResize, { passive: true });
        return () => {
            window.removeEventListener('resize', handleResize);
            try {
                document.fonts.removeEventListener?.('loadingdone', handleFontsLoaded);
            }
            catch {
                // ignore
            }
            debouncedUpdatePopoverPosition.cancel();
        };
    }, [alignment, placement, open, withTip]);
    return actualPlacement;
};
//# sourceMappingURL=use-popover-position.js.map